import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { ArrowLeftOutlined, CloseOutlined } from "@ant-design/icons";
import { loadChapter, type BibleBook, type BibleFootnote, type BibleVerse } from "../services/bible";
import {
  parseRefs,
  renderVerseText,
  getAnnotationFootnote,
  hasRenderableFootnoteNote,
  doesVerseOverlapRange,
  compareVersesByPosition,
  getBookRefsForVersion,
  type ModalDescriptor,
  type FootnoteData,
} from "../services/annotationRefs";

interface Props {
  versionId: string;
  books: BibleBook[];
  footnotes: BibleFootnote[];
  getBookName: (bookId: string) => string;
  stack: ModalDescriptor[];
  onClose: () => void;
  onBack: () => void;
  onPush: (m: ModalDescriptor) => void;
}

interface ChapterKey {
  bookId: string;
  chapter: number;
}

const toChapterKeys = (top: ModalDescriptor): ChapterKey[] => {
  const keys: ChapterKey[] = [];
  const push = (bookId: string, chapter: number | undefined) => {
    if (bookId && chapter != null) keys.push({ bookId, chapter });
  };
  if (top.verseRef) push(top.verseRef.bookId, top.verseRef.chapter);
  if (top.annRef) push(top.annRef.bookId, top.annRef.chapter);
  if (top.verseRanges) {
    for (const rg of top.verseRanges) {
      const bid = rg.bookId || top.bookId;
      for (let ch = rg.chapterStart; ch <= rg.chapterEnd; ch++) push(bid, ch);
    }
  }
  if (top.groupedParenEntries) {
    for (const entry of top.groupedParenEntries) {
      if (entry.kind === "verse") push(entry.bookId, entry.chapter);
      else if (entry.kind === "ann") push(entry.annRef.bookId, entry.annRef.chapter);
      else {
        for (const rg of entry.verseRanges) {
          const bid = rg.bookId || entry.bookId;
          for (let ch = rg.chapterStart; ch <= rg.chapterEnd; ch++) push(bid, ch);
        }
      }
    }
  }
  return keys;
};

const FootnoteModalStack: React.FC<Props> = ({
  versionId,
  books,
  footnotes,
  getBookName,
  stack,
  onClose,
  onBack,
  onPush,
}) => {
  const intl = useIntl();
  const nextModalId = useRef(0);
  const [loaded, setLoaded] = useState<Record<string, BibleVerse[]>>({});
  const [loadingKeys, setLoadingKeys] = useState<Record<string, boolean>>({});
  const top = stack[stack.length - 1];

  useEffect(() => {
    if (!top) {
      setLoaded({});
      return;
    }
    let cancelled = false;
    const keys = toChapterKeys(top);
    const cacheKey = (k: ChapterKey) => `${k.bookId}|${k.chapter}`;
    const missing = keys.filter((k) => !loaded[cacheKey(k)]);
    if (missing.length === 0) return;
    const nextLoading = { ...loadingKeys };
    for (const k of missing) nextLoading[cacheKey(k)] = true;
    setLoadingKeys(nextLoading);
    Promise.all(
      missing.map(async (k) => {
        try {
          const verses = await loadChapter(versionId, k.bookId, k.chapter);
          return { key: cacheKey(k), verses };
        } catch {
          return { key: cacheKey(k), verses: [] as BibleVerse[] };
        }
      })
    ).then((results) => {
      if (cancelled) return;
      const next: Record<string, BibleVerse[]> = {};
      for (const r of results) next[r.key] = r.verses;
      setLoaded((prev) => ({ ...prev, ...next }));
      const cleared: Record<string, boolean> = {};
      for (const k of missing) cleared[cacheKey(k)] = false;
      setLoadingKeys((prev) => ({ ...prev, ...cleared }));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top, versionId]);

  const pushFootnote = useCallback(
    (fn: FootnoteData) => {
      nextModalId.current += 1;
      onPush({
        id: `fn-${nextModalId.current}`,
        bookId: top?.bookId || "",
        chapter: top?.chapter,
        footnotes: [fn],
      });
    },
    [onPush, top]
  );

  const parseCtx = useMemo(() => {
    return {
      books,
      getBookName,
      pushModal: onPush,
      nextModalId,
      curBookId: top?.bookId || "",
      curChapter: top?.chapter || 1,
      curVerse: undefined,
      curFlag: undefined,
      sourceFootnoteId: undefined,
      sourceLocation: undefined,
    };
  }, [books, getBookName, onPush, top]);

  const renderVerseRow = (v: BibleVerse, bookId: string, chapter: number, keyPrefix: string) => {
    const f = String(v.Flag || "0");
    const flagUpper = intl.formatMessage({ id: "verse.flag.upper" });
    const flagMiddle = intl.formatMessage({ id: "verse.flag.middle" });
    const flagLower = intl.formatMessage({ id: "verse.flag.lower" });
    const fs = f === "1" ? flagUpper : f === "2" ? flagLower : f === "3" ? flagMiddle : "";
    return (
      <div key={`${v._id}-${f}`} className="verse-container verse-merged">
        <span className="verse-number">
          {v.Verse}
          {fs && <sup className="verse-half">{fs}</sup>}{" "}
        </span>
        {renderVerseText(v, footnotes, books, bookId, chapter, f, keyPrefix, (fn) => {
          nextModalId.current += 1;
          onPush({
            id: `fn-${nextModalId.current}`,
            bookId,
            chapter,
            footnotes: [fn],
          });
        })}
      </div>
    );
  };

  const renderContent = () => {
    if (!top) return null;
    const isFootnote = !!top.footnotes;
    const isGroupedParen = !!top.groupedParenEntries?.length;
    const isVerseRef = !!top.verseRef;
    const isVerseRanges = !!top.verseRanges;
    const isAnnRef = !!top.annRef && !isVerseRanges;

    const t = {
      loading: intl.formatMessage({ id: "common.loading" }),
      noVerseFound: intl.formatMessage({ id: "annotation.noVerseFound" }),
      noFootnoteFound: intl.formatMessage({ id: "annotation.noFootnoteFound" }),
    };

    const pendingLoad = (bookId: string, chapter: number) => {
      const key = `${bookId}|${chapter}`;
      return loadingKeys[key] === true || !loaded[key];
    };

    const chapterVerses = (bookId: string, chapter: number): BibleVerse[] => loaded[`${bookId}|${chapter}`] || [];

    if (isFootnote) {
      const headerText = `${getBookName(top.bookId)} ${top.chapter || 1}:${top.footnotes![0].verse} ${intl.formatMessage({ id: "reading.noteMark" })}${top.footnotes![0].seq}`;
      return (
        <>
          <div className="outline-modal-header">
            <h3>{headerText}</h3>
            <div className="outline-modal-actions">
              {stack.length > 1 && (
                <span className="outline-modal-back" onClick={onBack} title={intl.formatMessage({ id: "annotation.back" })}>
                  <ArrowLeftOutlined />
                </span>
              )}
              <span className="outline-modal-close" onClick={onClose}>
                <CloseOutlined />
              </span>
            </div>
          </div>
          <div className="outline-modal-body">
            {top.footnotes!.map((fn) => (
              <div key={fn._id || `${fn.verse}-${fn.seq}-${fn.location}`} className="footnote-item">
                <span className="footnote-item-seq">{fn.seq}</span>
                <div className="footnote-item-note">
                  {parseRefs(fn.note, {
                    ...parseCtx,
                    curBookId: top.bookId,
                    curChapter: top.chapter || 1,
                    curVerse: fn.verse,
                    curFlag: fn.flag,
                    sourceFootnoteId: fn._id,
                    sourceLocation: fn.location,
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    if (isGroupedParen) {
      return (
        <>
          <div className="outline-modal-header">
            <h3>{(top.groupedParenLabel || "").replace(/^（/, "").replace(/）$/, "")}</h3>
            <div className="outline-modal-actions">
              {stack.length > 1 && (
                <span className="outline-modal-back" onClick={onBack}>
                  <ArrowLeftOutlined />
                </span>
              )}
              <span className="outline-modal-close" onClick={onClose}>
                <CloseOutlined />
              </span>
            </div>
          </div>
          <div className="outline-modal-body">
            {top.groupedParenEntries!.map((entry, entryIndex) => {
              const entryTitle = (
                <div className="outline-chapter-title" style={{ cursor: "default", marginTop: entryIndex > 0 ? 16 : 0 }}>
                  {entry.label}
                </div>
              );
              if (entry.kind === "verse") {
                const verses = chapterVerses(entry.bookId, entry.chapter)
                  .filter((v) => String(v.Chapter) === String(entry.chapter) && String(v.Verse) === String(entry.verse))
                  .sort(compareVersesByPosition);
                return (
                  <React.Fragment key={entry.id}>
                    {entryTitle}
                    {verses.length === 0 ? (
                      <div className="outline-modal-empty">{pendingLoad(entry.bookId, entry.chapter) ? t.loading : t.noVerseFound}</div>
                    ) : (
                      verses.map((v) => renderVerseRow(v, entry.bookId, entry.chapter, `${v.Book}-${v.Chapter}-${v.Verse}-${v.Flag || "0"}-group-`))
                    )}
                  </React.Fragment>
                );
              }
              if (entry.kind === "ann") {
                const ar = entry.annRef;
                const annFootnote = getAnnotationFootnote(
                  footnotes,
                  books,
                  ar.bookId,
                  ar.chapter,
                  ar.verse,
                  ar.seq,
                  ar.flag,
                  ar.sourceFootnoteId,
                  ar.sourceLocation,
                  ar.endVerse
                );
                const verses = chapterVerses(ar.bookId, ar.chapter)
                  .filter((v) => String(v.Chapter) === String(ar.chapter) && String(v.Verse) === String(ar.verse))
                  .sort(compareVersesByPosition);
                const hasAnnotation = !!(annFootnote && hasRenderableFootnoteNote(annFootnote.note));
                const annFlag = String(annFootnote?.flag || ar.flag || "0");
                return (
                  <React.Fragment key={entry.id}>
                    {entryTitle}
                    {verses.length > 0 ? (
                      verses.map((v) => renderVerseRow(v, ar.bookId, ar.chapter, `${v.Book}-${v.Chapter}-${v.Verse}-${v.Flag || "0"}-group-ann-`))
                    ) : (
                      <div className="outline-modal-empty">{pendingLoad(ar.bookId, ar.chapter) ? t.loading : t.noVerseFound}</div>
                    )}
                    {hasAnnotation && annFootnote ? (
                      <div className="footnote-item">
                        <span className="footnote-item-seq">{annFootnote.seq}</span>
                        <div className="footnote-item-note">
                          {parseRefs(annFootnote.note, {
                            ...parseCtx,
                            curBookId: ar.bookId,
                            curChapter: ar.chapter,
                            curVerse: ar.verse,
                            curFlag: annFlag,
                            sourceFootnoteId: annFootnote._id,
                            sourceLocation: parseInt(annFootnote.location, 10) || 0,
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="outline-modal-empty">{t.noFootnoteFound}</div>
                    )}
                  </React.Fragment>
                );
              }
              // kind === "range"
              const rangeAnnRef = entry.annRef;
              const rangeAnnFootnote = rangeAnnRef
                ? getAnnotationFootnote(
                    footnotes,
                    books,
                    rangeAnnRef.bookId,
                    rangeAnnRef.chapter,
                    rangeAnnRef.verse,
                    rangeAnnRef.seq,
                    rangeAnnRef.flag,
                    rangeAnnRef.sourceFootnoteId,
                    rangeAnnRef.sourceLocation,
                    rangeAnnRef.endVerse
                  )
                : null;
              const hasRangeAnnotation = !!(rangeAnnFootnote && hasRenderableFootnoteNote(rangeAnnFootnote.note));
              const allVerses: { verse: BibleVerse; bookId: string; chapter: number }[] = [];
              for (const rg of entry.verseRanges) {
                const bid = rg.bookId || entry.bookId;
                const refs = getBookRefsForVersion(books, bid);
                for (let ch = rg.chapterStart; ch <= rg.chapterEnd; ch++) {
                  const chVerses = chapterVerses(bid, ch)
                    .filter((v) => refs.includes(String(v.Book)) || refs.includes(String(v._id)))
                    .filter((v) => doesVerseOverlapRange(rg, ch, parseInt(v.Verse, 10), v.Flag))
                    .sort(compareVersesByPosition);
                  for (const cv of chVerses) allVerses.push({ verse: cv, bookId: bid, chapter: ch });
                }
              }
              let lastChapter = -1;
              let lastBookId = "";
              return (
                <React.Fragment key={entry.id}>
                  {entryTitle}
                  {allVerses.length > 0 ? (
                    allVerses.map(({ verse: v, bookId: vBookId, chapter: vChapter }) => {
                      const ch = parseInt(v.Chapter, 10);
                      const showChapter = ch !== lastChapter || vBookId !== lastBookId;
                      lastChapter = ch;
                      lastBookId = vBookId;
                      return (
                        <React.Fragment key={v._id}>
                          {showChapter && (
                            <div className="outline-chapter-title" style={{ cursor: "default", marginTop: 16 }}>
                              {getBookName(vBookId)} {ch}
                            </div>
                          )}
                          {renderVerseRow(v, vBookId, vChapter, `${v.Book}-${v.Chapter}-${v.Verse}-${v.Flag || "0"}-group-range-`)}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <div className="outline-modal-empty">{t.noVerseFound}</div>
                  )}
                  {rangeAnnRef ? (
                    hasRangeAnnotation && rangeAnnFootnote ? (
                      <div className="footnote-item">
                        <span className="footnote-item-seq">{rangeAnnFootnote.seq}</span>
                        <div className="footnote-item-note">
                          {parseRefs(rangeAnnFootnote.note, {
                            ...parseCtx,
                            curBookId: rangeAnnRef.bookId,
                            curChapter: rangeAnnRef.chapter,
                            curVerse: rangeAnnRef.verse,
                            curFlag: String(rangeAnnFootnote.flag || rangeAnnRef.flag || "0"),
                            sourceFootnoteId: rangeAnnFootnote._id,
                            sourceLocation: parseInt(rangeAnnFootnote.location, 10) || 0,
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="outline-modal-empty">{t.noFootnoteFound}</div>
                    )
                  ) : null}
                </React.Fragment>
              );
            })}
          </div>
        </>
      );
    }

    if (isVerseRef) {
      const vr = top.verseRef!;
      const verses = chapterVerses(vr.bookId, vr.chapter)
        .filter((v) => String(v.Chapter) === String(vr.chapter) && String(v.Verse) === String(vr.verse))
        .sort(compareVersesByPosition);
      const headerText = `${getBookName(vr.bookId)} ${vr.chapter}:${vr.verse}`;
      return (
        <>
          <div className="outline-modal-header">
            <h3>{headerText}</h3>
            <div className="outline-modal-actions">
              {stack.length > 1 && (
                <span className="outline-modal-back" onClick={onBack}>
                  <ArrowLeftOutlined />
                </span>
              )}
              <span className="outline-modal-close" onClick={onClose}>
                <CloseOutlined />
              </span>
            </div>
          </div>
          <div className="outline-modal-body">
            {verses.length === 0 ? (
              <div className="outline-modal-empty">{pendingLoad(vr.bookId, vr.chapter) ? t.loading : t.noVerseFound}</div>
            ) : (
              verses.map((v) => renderVerseRow(v, vr.bookId, vr.chapter, `${v.Book}-${v.Chapter}-${v.Verse}-${v.Flag || "0"}-`))
            )}
          </div>
        </>
      );
    }

    if (isAnnRef) {
      const ar = top.annRef!;
      const annFootnote = getAnnotationFootnote(
        footnotes,
        books,
        ar.bookId,
        ar.chapter,
        ar.verse,
        ar.seq,
        ar.flag,
        ar.sourceFootnoteId,
        ar.sourceLocation,
        ar.endVerse
      );
      const verses = chapterVerses(ar.bookId, ar.chapter)
        .filter((v) => String(v.Chapter) === String(ar.chapter) && String(v.Verse) === String(ar.verse))
        .sort(compareVersesByPosition);
      const hasAnnotation = !!(annFootnote && hasRenderableFootnoteNote(annFootnote.note));
      const annFlag = String(annFootnote?.flag || ar.flag || "0");
      const headerText = ar.label || `${getBookName(ar.bookId)} ${ar.chapter}:${ar.verse} · ${intl.formatMessage({ id: "reading.noteMark" })}${ar.seq}`;
      return (
        <>
          <div className="outline-modal-header">
            <h3>{headerText}</h3>
            <div className="outline-modal-actions">
              {stack.length > 1 && (
                <span className="outline-modal-back" onClick={onBack}>
                  <ArrowLeftOutlined />
                </span>
              )}
              <span className="outline-modal-close" onClick={onClose}>
                <CloseOutlined />
              </span>
            </div>
          </div>
          <div className="outline-modal-body">
            {verses.length > 0 ? (
              verses.map((v) => renderVerseRow(v, ar.bookId, ar.chapter, `${v.Book}-${v.Chapter}-${v.Verse}-${v.Flag || "0"}-ann-`))
            ) : (
              <div className="outline-modal-empty">{pendingLoad(ar.bookId, ar.chapter) ? t.loading : t.noVerseFound}</div>
            )}
            {hasAnnotation && annFootnote ? (
              <div className="footnote-item">
                <span className="footnote-item-seq">{annFootnote.seq}</span>
                <div className="footnote-item-note">
                  {parseRefs(annFootnote.note, {
                    ...parseCtx,
                    curBookId: ar.bookId,
                    curChapter: ar.chapter,
                    curVerse: ar.verse,
                    curFlag: annFlag,
                    sourceFootnoteId: annFootnote._id,
                    sourceLocation: parseInt(annFootnote.location, 10) || 0,
                  })}
                </div>
              </div>
            ) : (
              <div className="outline-modal-empty">{t.noFootnoteFound}</div>
            )}
          </div>
        </>
      );
    }

    if (isVerseRanges) {
      const rangeAnnRef = top.annRef;
      const rangeAnnFootnote = rangeAnnRef
        ? getAnnotationFootnote(
            footnotes,
            books,
            rangeAnnRef.bookId,
            rangeAnnRef.chapter,
            rangeAnnRef.verse,
            rangeAnnRef.seq,
            rangeAnnRef.flag,
            rangeAnnRef.sourceFootnoteId,
            rangeAnnRef.sourceLocation,
            rangeAnnRef.endVerse
          )
        : null;
      const hasRangeAnnotation = !!(rangeAnnFootnote && hasRenderableFootnoteNote(rangeAnnFootnote.note));
      const allVerses: { verse: BibleVerse; bookId: string; chapter: number }[] = [];
      for (const rg of top.verseRanges!) {
        const bid = rg.bookId || top.bookId;
        const refs = getBookRefsForVersion(books, bid);
        for (let ch = rg.chapterStart; ch <= rg.chapterEnd; ch++) {
          const chVerses = chapterVerses(bid, ch)
            .filter((v) => refs.includes(String(v.Book)) || refs.includes(String(v._id)))
            .filter((v) => doesVerseOverlapRange(rg, ch, parseInt(v.Verse, 10), v.Flag))
            .sort(compareVersesByPosition);
          for (const cv of chVerses) allVerses.push({ verse: cv, bookId: bid, chapter: ch });
        }
      }
      const headerText = (top.rangeLabel || "").replace(/^（/, "").replace(/）$/, "");
      let lastChapter = -1;
      let lastBookId = "";
      return (
        <>
          <div className="outline-modal-header">
            <h3>{headerText}</h3>
            <div className="outline-modal-actions">
              {stack.length > 1 && (
                <span className="outline-modal-back" onClick={onBack}>
                  <ArrowLeftOutlined />
                </span>
              )}
              <span className="outline-modal-close" onClick={onClose}>
                <CloseOutlined />
              </span>
            </div>
          </div>
          <div className="outline-modal-body">
            {allVerses.length > 0 ? (
              allVerses.map(({ verse: v, bookId: vBookId, chapter: vChapter }) => {
                const ch = parseInt(v.Chapter, 10);
                const showChapter = ch !== lastChapter || vBookId !== lastBookId;
                lastChapter = ch;
                lastBookId = vBookId;
                return (
                  <React.Fragment key={v._id}>
                    {showChapter && (
                      <div className="outline-chapter-title" style={{ cursor: "default", marginTop: 16 }}>
                        {getBookName(vBookId)} {ch}
                      </div>
                    )}
                    {renderVerseRow(v, vBookId, vChapter, `${v.Book}-${v.Chapter}-${v.Verse}-${v.Flag || "0"}-`)}
                  </React.Fragment>
                );
              })
            ) : (
              <div className="outline-modal-empty">{t.noVerseFound}</div>
            )}
            {rangeAnnRef ? (
              hasRangeAnnotation && rangeAnnFootnote ? (
                <div className="footnote-item">
                  <span className="footnote-item-seq">{rangeAnnFootnote.seq}</span>
                  <div className="footnote-item-note">
                    {parseRefs(rangeAnnFootnote.note, {
                      ...parseCtx,
                      curBookId: rangeAnnRef.bookId,
                      curChapter: rangeAnnRef.chapter,
                      curVerse: rangeAnnRef.verse,
                      curFlag: String(rangeAnnFootnote.flag || rangeAnnRef.flag || "0"),
                      sourceFootnoteId: rangeAnnFootnote._id,
                      sourceLocation: parseInt(rangeAnnFootnote.location, 10) || 0,
                    })}
                  </div>
                </div>
              ) : (
                <div className="outline-modal-empty">{t.noFootnoteFound}</div>
              )
            ) : null}
          </div>
        </>
      );
    }

    return null;
  };

  if (!top) return null;
  return (
    <div className="outline-modal-overlay" onClick={onClose}>
      <div className="outline-modal" onClick={(e) => e.stopPropagation()}>
        {renderContent()}
      </div>
    </div>
  );
};

export default FootnoteModalStack;
